import { cva } from 'class-variance-authority';
import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { ButtonVariantType } from './types';

export function cn(...inputs: ClassValue[]) {
	return twMerge(clsx(inputs));
}

export const buttonVariants = cva(
	'wlcb-inline-flex wlcb-items-center wlcb-justify-center wlcb-whitespace-nowrap wlcb-rounded-md wlcb-text-button wlcb-font-[500] wlcb-ring-offset-background wlcb-transition-colors focus-visible:wlcb-outline-none focus-visible:wlcb-ring-2 focus-visible:wlcb-ring-ring focus-visible:wlcb-ring-offset-2 disabled:wlcb-pointer-events-none disabled:wlcb-opacity-50',
	{
		variants: {
			variant: {
				primary:
					'wlcb-bg-primary wlcb-text-primary-foreground hover:wlcb-bg-primary-hover active:wlcb-bg-primary-pressed',
				destructive:
					'wlcb-bg-destructive wlcb-text-destructive-foreground hover:wlcb-bg-destructive/90',
				outline:
					'wlcb-border wlcb-border-primary hover:wlcb-border-primary-hover wlcb-bg-surface hover:wlcb-bg-surface-hover wlcb-text-primary active:wlcb-border-primary-pressed',
				secondary:
					'wlcb-bg-secondary wlcb-text-secondary-foreground hover:wlcb-bg-secondary-hover',
				ghost:
					'wlcb-text-primary hover:wlcb-bg-primary hover:wlcb-text-primary-foreground',
			},
			size: {
				sm: 'wlcb-h-8 wlcb-w-fit wlcb-px-space-075 wlcb-py-space-100',
				md: 'wlcb-h-10 wlcb-w-fit wlcb-px-space-075 wlcb-py-space-100',
				lg: 'wlcb-h-12 wlcb-w-fit wlcb-px-space-075 wlcb-py-space-100',
				xl: 'wlcb-h-16 wlcb-w-fit wlcb-px-space-075 wlcb-py-space-100 wlcb-rounded-none',
			},
		},
		defaultVariants: {
			variant: ButtonVariantType.Primary,
			size: 'md',
		},
	}
);

export const lightenColor = ({
	color,
	percent,
}: {
	color: string;
	percent: number;
}) => {
	const num = parseInt(color.replace('#', ''), 16);
	const amt = Math.round(2.55 * percent);
	const R = (num >> 16) + amt;
	const G = ((num >> 8) & 0x00ff) + amt;
	const B = (num & 0x0000ff) + amt;
	return `#${(0x1000000 + (R < 255 ? (R < 1 ? 0 : R) : 255) * 0x10000 + (G < 255 ? (G < 1 ? 0 : G) : 255) * 0x100 + (B < 255 ? (B < 1 ? 0 : B) : 255)).toString(16).slice(1).toUpperCase()}`;
};

export const darkenColor = ({
	color,
	percent,
}: {
	color: string;
	percent: number;
}) => {
	const num = parseInt(color.replace('#', ''), 16);
	const amt = Math.round(2.55 * percent);
	const R = (num >> 16) - amt;
	const G = ((num >> 8) & 0x00ff) - amt;
	const B = (num & 0x0000ff) - amt;
	return `#${(0x1000000 + (R < 255 ? (R < 1 ? 0 : R) : 255) * 0x10000 + (G < 255 ? (G < 1 ? 0 : G) : 255) * 0x100 + (B < 255 ? (B < 1 ? 0 : B) : 255)).toString(16).slice(1).toUpperCase()}`;
};

export const getLuminance = ({ color }: { color: string }) => {
	const rgb = parseInt(color.replace('#', ''), 16);
	const r = (rgb >> 16) & 0xff;
	const g = (rgb >> 8) & 0xff;
	const b = (rgb >> 0) & 0xff;

	const luminance = 0.299 * r + 0.587 * g + 0.114 * b;
	return luminance;
};

export const isColorDark = ({ color }: { color: string }) =>
	getLuminance({ color }) < 140;

const setThemeProperty = (
	elementId: string,
	property: string,
	value: string
) => {
	if (typeof document !== 'undefined') {
		document.getElementById(elementId)?.style.setProperty(property, value);
	}
};

export const applyThemeColors = (
	elementId: string,
	themeColor: { color: string; textColor: string },
	isPrimary: boolean
) => {
	const lightColor = lightenColor({ color: themeColor.color, percent: 10 });
	const darkColor = darkenColor({ color: themeColor.color, percent: 10 });
	const isDark = isColorDark({ color: themeColor.color });

	const colorType = isPrimary ? 'primary' : 'secondary';

	setThemeProperty(elementId, `--theme-brand-${colorType}`, themeColor.color);
	setThemeProperty(
		elementId,
		`--theme-brand-${colorType}-hover`,
		isDark ? lightColor : darkColor
	);
	setThemeProperty(
		elementId,
		`--theme-brand-${colorType}-pressed`,
		isDark ? darkColor : lightColor
	);
	setThemeProperty(
		elementId,
		`--theme-brand-${colorType}-text-color`,
		themeColor.textColor
	);
};
